import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import PredictionPaymentModal from '../components/PredictionPaymentModal';
import {
  RadialBarChart, RadialBar,
} from 'recharts';
import {
  Bot, TrendingUp, Zap, Clock, ChevronDown, ChevronUp, RefreshCw,
  Shield, History, BarChart3, Flame, Snowflake, ArrowLeft, Brain,
  Target, Activity, Layers, Eye,
} from 'lucide-react';

const lotteryLogos = {
  'Gana Mas': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/l2etb5gf_gana-mas.png',
  'Loteria Nacional': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/tmvw86yr_7827e5ee87f05e864aa79f64210269c5.png',
  'Quiniela Leidsa': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/kh7pktnz_quiniela-leidsa.png',
  'Quiniela Real': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/fx8q4po4_loteria-real.png',
  'Quiniela Loteka': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/f0ftl49v_quiniela-loteka.png',
  'Florida Dia': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/3bjyf4yg_florida-dia.png',
  'Florida Noche': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/3bjyf4yg_florida-dia.png',
  'New York Tarde': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/la8m41e6_new-york-noche.png',
  'New York Noche': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/la8m41e6_new-york-noche.png',
  'La Primera Dia': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/own2l5v1_la-primera-dia.png',
  'Primera Noche': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/own2l5v1_la-primera-dia.png',
  'La Suerte 12:30': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vz6qo477_la-suerte-dia.png',
  'La Suerte 18:00': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vz6qo477_la-suerte-dia.png',
  'Quiniela LoteDom': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/g0saehi3_d46144f7e1f664fee5ba811f984c246e.png',
  'Anguila Manana': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vg0ch0n1_anguila-manana-10am.png',
  'Anguila Medio Dia': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vg0ch0n1_anguila-manana-10am.png',
  'Anguila Tarde': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vg0ch0n1_anguila-manana-10am.png',
  'Anguila Noche': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vg0ch0n1_anguila-manana-10am.png',
  'King Lottery 12:30': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/y8q9q99k_quiniela-king-lottery-dia.png',
  'King Lottery 7:30': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/y8q9q99k_quiniela-king-lottery-dia.png',
};

// Try to match logo with fuzzy name matching
const getLogo = (name) => {
  if (lotteryLogos[name]) return lotteryLogos[name];
  // Remove accents for matching
  const normalized = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return lotteryLogos[normalized] || null;
};

const companyColors = {
  'Nacional': '#22c55e', 'Leidsa': '#eab308', 'Loteria Real': '#dc2626',
  'Loteka': '#2563eb', 'Americanas': '#1e3a8a', 'La Primera': '#dc2626',
  'La Suerte': '#3b82f6', 'LoteDom': '#1e3a8a', 'Anguila': '#f97316', 'King Lottery': '#6b7280',
};

const FACTOR_META = {
  terminal: { label: 'Terminal', color: '#f59e0b', icon: Target },
  transition: { label: 'Transicion', color: '#3b82f6', icon: Activity },
  rsi: { label: 'RSI', color: '#8b5cf6', icon: TrendingUp },
  cycle: { label: 'Ciclo', color: '#ef4444', icon: Clock },
  echo: { label: 'Eco', color: '#22c55e', icon: Zap },
  position: { label: 'Posicion', color: '#06b6d4', icon: Layers },
  day: { label: 'Dia', color: '#f97316', icon: BarChart3 },
  pair: { label: 'Pares', color: '#ec4899', icon: Brain },
  decade: { label: 'Decada', color: '#14b8a6', icon: BarChart3 },
  mirror: { label: 'Espejo', color: '#a855f7', icon: Eye },
};

// Confidence meter
const ConfidenceMeter = ({ value }) => {
  const color = value >= 70 ? '#22c55e' : value >= 50 ? '#eab308' : '#ef4444';
  const data = [{ value, fill: color }];
  return (
    <div className="relative w-20 h-20" data-testid="confidence-meter">
      <RadialBarChart cx={40} cy={40} innerRadius={28} outerRadius={38} barSize={8} data={data} startAngle={90} endAngle={-270} width={80} height={80}>
        <RadialBar background clockWise dataKey="value" cornerRadius={10} />
      </RadialBarChart>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold" style={{ color }}>{value}%</span>
      </div>
    </div>
  );
};

// Factor bar
const FactorBar = ({ name, value }) => {
  const meta = FACTOR_META[name] || { label: name, color: '#6b7280' };
  const width = Math.min(value, 100);
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-slate-400 w-16 text-right truncate">{meta.label}</span>
      <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${width}%`, backgroundColor: meta.color }} />
      </div>
      <span className="text-[10px] font-mono text-slate-500 w-7 text-right">{Math.round(value)}</span>
    </div>
  );
};

// Number detail card
const NumberDetail = ({ exp, idx }) => {
  const ballColors = ['#22c55e', '#eab308', '#3b82f6'];
  const trendIcons = { rising: '++', falling: '--', stable: '~' };
  const trendColors = { rising: 'text-green-400', falling: 'text-red-400', stable: 'text-slate-400' };
  const bd = exp.breakdown || {};

  return (
    <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700/30" data-testid={`number-detail-${exp.number}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-black shadow-lg"
          style={{ backgroundColor: ballColors[idx], boxShadow: `0 0 15px ${ballColors[idx]}40` }}>
          {String(exp.number).padStart(2, '0')}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-semibold">{exp.range}</span>
            <span className={`text-xs font-bold ${trendColors[exp.trend] || trendColors.stable}`}>
              {trendIcons[exp.trend] || '~'} {exp.trend === 'rising' ? 'ALZA' : exp.trend === 'falling' ? 'BAJA' : 'ESTABLE'}
            </span>
          </div>
          <span className="text-slate-500 text-[11px]">Score: {exp.score?.toFixed(1)} | RSI: {exp.rsi?.toFixed(0)}</span>
        </div>
      </div>

      {/* Explanation text */}
      <p className="text-xs text-slate-300 bg-slate-900/60 rounded-lg px-3 py-2 mb-3 leading-relaxed">
        {exp.explanation}
      </p>

      {/* Factor breakdown bars */}
      <div className="space-y-1">
        {Object.entries(bd).filter(([_, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([key, val]) => (
          <FactorBar key={key} name={key} value={val} />
        ))}
      </div>

      {/* Meta stats */}
      <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-700/30">
        <div className="text-center">
          <div className="text-green-400 font-bold text-xs">{exp.freq_7d || 0}</div>
          <div className="text-[9px] text-slate-500">7 dias</div>
        </div>
        <div className="text-center">
          <div className="text-blue-400 font-bold text-xs">{exp.freq_30d || 0}</div>
          <div className="text-[9px] text-slate-500">30 dias</div>
        </div>
        <div className="text-center">
          <div className="text-amber-400 font-bold text-xs">{exp.current_gap || 0}</div>
          <div className="text-[9px] text-slate-500">Brecha</div>
        </div>
        <div className="text-center">
          <div className="text-purple-400 font-bold text-xs">{exp.appearances || 0}</div>
          <div className="text-[9px] text-slate-500">Total</div>
        </div>
      </div>
    </div>
  );
};

// Prediction Card
const PredictionCard = ({ prediction, onExpand, expanded }) => {
  const logoUrl = getLogo(prediction.lottery_name);
  const ballColors = ['#22c55e', '#eab308', '#3b82f6'];
  const rangeLabels = ['BAJO', 'MEDIO', 'ALTO'];

  return (
    <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-slate-600 transition-all"
      data-testid={`bot-card-${prediction.lottery_name}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-700/30">
        <div className="w-10 h-10 rounded-lg bg-gray-900 overflow-hidden shrink-0">
          {logoUrl && <img src={logoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm truncate">{prediction.lottery_name}</h3>
          <p className="text-slate-400 text-xs flex items-center gap-1">
            <Clock size={11} /> {prediction.draw_time}
            {prediction.source === 'bot-diario-v4' && (
              <span className="ml-1 px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[9px] font-bold">V4</span>
            )}
          </p>
        </div>
        <ConfidenceMeter value={prediction.confidence} />
      </div>

      {/* Numbers */}
      <div className="p-4">
        <div className="flex justify-center gap-4 mb-3">
          {prediction.numbers.map((num, idx) => (
            <div key={`num-${idx}`} className="text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg"
                style={{ backgroundColor: ballColors[idx], boxShadow: `0 0 20px ${ballColors[idx]}40` }}>
                {String(num).padStart(2, '0')}
              </div>
              <span className="text-[10px] text-slate-500 mt-1 block">{rangeLabels[idx]}</span>
            </div>
          ))}
        </div>

        {/* Summary explanation */}
        {prediction.explanation && (
          <p className="text-xs text-slate-400 bg-slate-900/40 rounded-lg px-3 py-2 mb-2 leading-relaxed line-clamp-2">
            {prediction.explanation}
          </p>
        )}

        {/* Quick stats */}
        <div className="flex justify-between text-xs text-slate-400 bg-slate-900/50 rounded-lg px-3 py-2">
          <span>{prediction.summary?.parity}</span>
          <span>T: {prediction.summary?.terminals?.join(',')}</span>
          <span>Suma: {prediction.summary?.sum}</span>
          <span>{prediction.summary?.draws_analyzed} sorteos</span>
        </div>
      </div>

      {/* Expand button */}
      <button onClick={() => onExpand(prediction.lottery_id)}
        className="w-full py-2.5 text-xs text-slate-400 hover:text-white hover:bg-slate-700/30 transition-all flex items-center justify-center gap-1 border-t border-slate-700/30"
        data-testid={`expand-${prediction.lottery_name}`}>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {expanded ? 'Ocultar analisis V4' : 'Ver analisis V4 completo'}
      </button>

      {/* Expanded V4 details */}
      {expanded && prediction.explanations && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-700/20 bg-slate-900/30">
          <div className="pt-3 flex items-center gap-2 mb-2">
            <Brain size={14} className="text-purple-400" />
            <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">
              V4 Ultimate — {prediction.summary?.formula?.split(':')[0] || '10 Capas'}
            </span>
          </div>
          {prediction.explanations.map((exp, idx) => (
            <NumberDetail key={`exp-${exp.number}`} exp={exp} idx={idx} />
          ))}

          {/* Hot / Cold */}
          {(prediction.hot_numbers?.length > 0 || prediction.cold_numbers?.length > 0) && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              {prediction.hot_numbers?.length > 0 && (
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Flame size={12} className="text-red-400" />
                    <span className="text-[10px] text-red-400 font-semibold uppercase">Calientes</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {prediction.hot_numbers.slice(0, 6).map(h => (
                      <span key={`hot-${h.number}`} className="w-7 h-7 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-[10px] font-bold">
                        {h.number}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {prediction.cold_numbers?.length > 0 && (
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Snowflake size={12} className="text-cyan-400" />
                    <span className="text-[10px] text-cyan-400 font-semibold uppercase">Vencidos</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {prediction.cold_numbers.slice(0, 6).map(c => (
                      <span key={`cold-${c.number}`} className="w-7 h-7 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-[10px] font-bold">
                        {c.number}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Page
export default function BotDiario() {
  const { token } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('today');
  const [showPayment, setShowPayment] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bot/today`, { headers });
      setPredictions(res.data.predictions || []);
    } catch (err) {
      if (err.response?.status === 402) { setShowPayment(true); }
      else { toast.error('Error al cargar predicciones'); }
    } finally { setLoading(false); }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bot/stats`, { headers });
      setStats(res.data);
    } catch (err) { console.warn('Stats fetch failed:', err.message); }
  }, [token]);

  useEffect(() => { fetchToday(); fetchStats(); }, [fetchToday, fetchStats]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/bot/generate-now`, {}, { headers });
      setPredictions(res.data.predictions || []);
      toast.success('Predicciones V4 regeneradas');
    } catch (err) {
      toast.error('Error al regenerar');
    } finally { setRefreshing(false); }
  };

  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  // Sort: earliest draw time first (memoized to avoid re-sort on every render)
  const sortedPreds = useMemo(
    () => [...predictions].sort((a, b) => (a.draw_time || '').localeCompare(b.draw_time || '')),
    [predictions]
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="bot-diario-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors" data-testid="back-link">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Bot size={22} className="text-emerald-400" />
                Bot Diario
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full text-[10px] font-bold">V4 ULTIMATE</span>
              </h1>
              <p className="text-slate-500 text-xs">10 capas analiticas | {sortedPreds.length} loterias | {new Date().toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleRefresh} disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 text-xs hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
              data-testid="refresh-btn">
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Generando V4...' : 'Regenerar'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'today', label: 'Hoy', icon: Zap },
            { id: 'stats', label: 'Estadisticas', icon: BarChart3 },
            { id: 'history', label: 'Historial', icon: History },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'text-slate-500 hover:text-white hover:bg-slate-800'
              }`} data-testid={`tab-${tab.id}`}>
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats bar */}
        {stats && activeTab === 'today' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="stats-bar">
            <div className="bg-slate-800/50 rounded-xl p-3 text-center border border-slate-700/30">
              <p className="text-2xl font-black text-white">{stats.total_predictions}</p>
              <p className="text-[10px] text-slate-500 uppercase">Predicciones</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-3 text-center border border-slate-700/30">
              <p className="text-2xl font-black text-green-400">{stats.rate_1_plus}%</p>
              <p className="text-[10px] text-slate-500 uppercase">1+ Acierto</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-3 text-center border border-slate-700/30">
              <p className="text-2xl font-black text-amber-400">{stats.rate_2_plus}%</p>
              <p className="text-[10px] text-slate-500 uppercase">2+ Aciertos</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-3 text-center border border-slate-700/30">
              <p className="text-2xl font-black text-purple-400">{stats.rate_3_exact}%</p>
              <p className="text-[10px] text-slate-500 uppercase">3 Exactos</p>
            </div>
          </div>
        )}

        {/* V4 Engine Badge */}
        {activeTab === 'today' && (
          <div className="mb-6 p-3 rounded-xl bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-emerald-500/10 border border-purple-500/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Brain size={20} className="text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Motor V4 Ultimate</p>
                <p className="text-[11px] text-slate-400">Terminal + Transicion Markov + RSI + Ciclo + Eco + Posicion + Dia + Pares + Decada + Espejo</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-emerald-400">{sortedPreds.length}</p>
                <p className="text-[10px] text-slate-500">loterias</p>
              </div>
            </div>
          </div>
        )}

        {/* Today tab */}
        {activeTab === 'today' && (
          loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mb-4 animate-pulse">
                <Bot size={28} className="text-emerald-400" />
              </div>
              <p className="text-slate-400 text-sm">Analizando 10 capas para {predictions.length || 20} loterias...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedPreds.map(pred => (
                <PredictionCard key={pred.id || pred.lottery_id} prediction={pred}
                  onExpand={toggleExpand} expanded={expandedId === pred.lottery_id} />
              ))}
            </div>
          )
        )}

        {/* Stats tab */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700/30 text-center">
              <h2 className="text-2xl font-bold text-white mb-6">Rendimiento del Motor V4</h2>
              <div className="grid grid-cols-3 gap-8">
                <div>
                  <div className="text-5xl font-black text-green-400">{stats.rate_1_plus}%</div>
                  <p className="text-slate-400 mt-2">1+ numero acertado</p>
                  <p className="text-slate-600 text-sm">{stats.hit_1_plus} de {stats.total_predictions}</p>
                </div>
                <div>
                  <div className="text-5xl font-black text-amber-400">{stats.rate_2_plus}%</div>
                  <p className="text-slate-400 mt-2">2+ numeros acertados</p>
                  <p className="text-slate-600 text-sm">{stats.hit_2_plus} de {stats.total_predictions}</p>
                </div>
                <div>
                  <div className="text-5xl font-black text-purple-400">{stats.rate_3_exact}%</div>
                  <p className="text-slate-400 mt-2">3 numeros exactos</p>
                  <p className="text-slate-600 text-sm">{stats.hit_3_exact} de {stats.total_predictions}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/30">
              <h3 className="text-lg font-semibold text-white mb-4">Como funciona el Motor V4</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(FACTOR_META).map(([key, meta]) => (
                  <div key={key} className="p-3 rounded-xl bg-slate-900/50 border border-slate-700/20 text-center">
                    <meta.icon size={18} style={{ color: meta.color }} className="mx-auto mb-1" />
                    <p className="text-xs font-semibold text-white">{meta.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* History tab */}
        {activeTab === 'history' && (
          <HistoryTab token={token} />
        )}
      </div>

      {/* Disclaimer */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-start gap-2 text-slate-600 text-xs">
          <Shield size={14} className="mt-0.5 shrink-0" />
          <p>V4 Ultimate Engine analiza 500+ sorteos con 10 capas matematicas. Las predicciones se basan en analisis estadistico avanzado y NO garantizan resultados. Juegue responsablemente.</p>
        </div>
      </div>

      <PredictionPaymentModal isOpen={showPayment} onClose={() => setShowPayment(false)} onSuccess={() => { setShowPayment(false); fetchToday(); }} />
    </div>
  );
}

// History Tab Component
function HistoryTab({ token }) {
  const [history, setHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/bot/history?days=14`, { headers });
        setHistory(res.data.history || {});
      } catch (e) { toast.error('Error al cargar historial'); }
      finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <p className="text-slate-500 text-center py-12">Cargando historial...</p>;

  const dates = Object.keys(history).sort().reverse();

  return (
    <div className="space-y-6" data-testid="history-tab">
      {dates.length === 0 ? (
        <p className="text-slate-500 text-center py-12">Sin historial disponible</p>
      ) : dates.map(date => (
        <div key={date}>
          <h3 className="text-sm font-semibold text-slate-400 mb-3">{new Date(date + 'T12:00:00').toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {history[date].map((p, idx) => (
              <div key={p.id || `hist-${idx}`} className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/20">
                <p className="text-xs text-slate-400 font-medium truncate">{p.lottery_name}</p>
                <div className="flex gap-1.5 mt-1">
                  {p.numbers?.map((n, i) => (
                    <span key={`hn-${i}`} className="w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-bold">
                      {n}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-slate-600 mt-1">{p.confidence}% conf</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
